import { UseInfiniteQueryResult, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { navigateWithTransition } from '~/core/utils/viewTransition/navigateWithTransition';
import { getMKQueryKeyFn } from '~/hooks/api/media-kits/get';
import {
  MEDIA_KIT_PAGE_SIZE,
  MediaKitListResp,
} from '~/hooks/api/media-kits/list';
import { getFetchCreatorKeyFn } from '~/hooks/api/useFetchCreator';
import { useGetCreatorProfile } from '~/hooks/api/useGetCreatorProfile';

import { AnyBlock } from '../PageBuilder/types';
import {
  ItemContainer,
  ListContainer,
} from '../SavedListsScreen/SavedListsScreen.styles';
import { AllShareableEmptyList } from './AllShareableEmptyList';
import CustomShareableCard from './CustomShareableCard';
import { MediaKitsLoadingGrid } from './MediaKitsLoadingGrid';
import { useMediaKitsGridState } from './store/useScrollPosition';

export const MediaKitsGrid = ({
  mediaKits,
}: {
  mediaKits: UseInfiniteQueryResult<MediaKitListResp, unknown>;
}) => {
  const qc = useQueryClient();
  const router = useRouter();
  const { state, setState } = useMediaKitsGridState();
  const creatorSlug = router.query['creatorSlug'] as string;
  const creator = useGetCreatorProfile({ slug: creatorSlug });

  const fetchNextPage = useCallback(() => {
    if (
      !mediaKits.hasNextPage ||
      mediaKits.isFetching ||
      mediaKits.isFetchingNextPage
    )
      return;
    mediaKits.fetchNextPage();
  }, [mediaKits]);

  const mediaKitsList = useMemo(() => {
    return mediaKits.data?.pages.map((page) => page.results).flat() || [];
  }, [mediaKits]);

  if (!mediaKitsList.length)
    return <AllShareableEmptyList creator={creator.data} />;

  return (
    <>
      <VirtuosoGrid
        components={{
          // @ts-ignore
          Item: ItemContainer,
          // @ts-ignore
          List: ListContainer,
        }}
        data={mediaKitsList}
        useWindowScroll
        restoreStateFrom={state}
        initialItemCount={Math.min(
          mediaKitsList.length,
          MEDIA_KIT_PAGE_SIZE - 4, // "-4" because virtuoso calls second page when last item is rendered, having 4 gives a better ux
        )}
        endReached={fetchNextPage}
        overscan={300}
        stateChanged={setState}
        itemContent={(_index, mediaKit) =>
          mediaKit && (
            <Link
              key={mediaKit.id}
              onClick={(e) => {
                e.preventDefault();

                navigateWithTransition(
                  router,
                  `/media-kits/${mediaKit.id}/builder`,
                );
              }}
              href={`/media-kits/${mediaKit.id}/builder`}
              onMouseEnter={async () => {
                // Pre-fetch media kit
                const mk = await qc.fetchQuery({
                  ...getMKQueryKeyFn(mediaKit.id),
                  staleTime: 5 * 60 * 1000,
                });

                // Pre-fetch creator info
                const talentBlock = mk?.blocks?.find(
                  (b: AnyBlock) => b.component === 'TalentProfile',
                );
                if (talentBlock?.properties?.creator?.id) {
                  await qc.fetchQuery({
                    ...getFetchCreatorKeyFn(
                      talentBlock?.properties?.creator?.id,
                    ),
                    staleTime: 5 * 60 * 1000,
                  });
                }
              }}
            >
              <CustomShareableCard {...mediaKit} />
            </Link>
          )
        }
      />
      {mediaKits.isFetchingNextPage && <MediaKitsLoadingGrid />}
    </>
  );
};
